import type { StaffUser, StaffRole, StaffPermissions } from '@/types/staff';

/**
 * Check if staff user has a specific permission
 */
export const hasPermission = (
  user: StaffUser | null,
  permission: keyof StaffPermissions
): boolean => {
  if (!user || user.status !== 'active') return false;
  return user[permission] === true;
};

/**
 * Check if user can view customers
 */
export const canViewCustomers = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_view_customers');
};

/**
 * Check if user can edit customers
 */
export const canEditCustomers = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_edit_customers');
};

/**
 * Check if user can create cases
 */
export const canCreateCases = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_create_cases');
};

/**
 * Check if user can close cases
 */
export const canCloseCases = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_close_cases');
};

/**
 * Check if user can send SMS
 */
export const canSendSMS = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_send_sms');
};

/**
 * Check if user can view telemetry
 */
export const canViewTelemetry = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_view_telemetry');
};

/**
 * Check if user can manage staff
 */
export const canManageStaff = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_manage_staff');
};

/**
 * Check if user can configure system
 */
export const canConfigureSystem = (user: StaffUser | null): boolean => {
  return hasPermission(user, 'can_configure_system');
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user: StaffUser | null, role: StaffRole): boolean => {
  if (!user || user.status !== 'active') return false;
  return user.role === role;
};

/**
 * Check if user is administrator
 */
export const isAdmin = (user: StaffUser | null): boolean => {
  return hasRole(user, 'administrator');
};

/**
 * Check if user is operations manager or admin
 */
export const isOperationsOrAdmin = (user: StaffUser | null): boolean => {
  if (!user || user.status !== 'active') return false;
  return user.role === 'operations_manager' || user.role === 'administrator';
};

/**
 * Check if user is compliance officer or admin
 */
export const isComplianceOrAdmin = (user: StaffUser | null): boolean => {
  if (!user || user.status !== 'active') return false;
  return user.role === 'compliance_officer' || user.role === 'administrator';
};

/**
 * Get default permissions for a role
 */
export const getDefaultPermissionsForRole = (role: StaffRole): StaffPermissions => {
  const permissions: Record<StaffRole, StaffPermissions> = {
    cs_rep: {
      can_view_customers: true,
      can_edit_customers: false,
      can_create_cases: true,
      can_close_cases: false,
      can_send_sms: false,
      can_view_telemetry: false,
      can_manage_staff: false,
      can_configure_system: false,
    },
    relationship_manager: {
      can_view_customers: true,
      can_edit_customers: true,
      can_create_cases: true,
      can_close_cases: true,
      can_send_sms: true,
      can_view_telemetry: false,
      can_manage_staff: false,
      can_configure_system: false,
    },
    compliance_officer: {
      can_view_customers: true,
      can_edit_customers: true,
      can_create_cases: true,
      can_close_cases: true,
      can_send_sms: false,
      can_view_telemetry: false,
      can_manage_staff: false,
      can_configure_system: false,
    },
    operations_manager: {
      can_view_customers: true,
      can_edit_customers: true,
      can_create_cases: true,
      can_close_cases: true,
      can_send_sms: true,
      can_view_telemetry: true,
      can_manage_staff: false,
      can_configure_system: true,
    },
    administrator: {
      can_view_customers: true,
      can_edit_customers: true,
      can_create_cases: true,
      can_close_cases: true,
      can_send_sms: true,
      can_view_telemetry: true,
      can_manage_staff: true,
      can_configure_system: true,
    },
  };

  return permissions[role];
};

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (user: StaffUser | null, route: string): boolean => {
  if (!user || user.status !== 'active') return false;

  // Route-based permissions
  const routePermissions: Record<string, (user: StaffUser) => boolean> = {
    '/customers': (u) => u.can_view_customers,
    '/cases': (u) => u.can_create_cases || u.can_close_cases,
    '/telemetry': (u) => u.can_view_telemetry || isOperationsOrAdmin(u),
    '/system': (u) => isOperationsOrAdmin(u),
    '/settings/staff': (u) => u.can_manage_staff,
    '/settings/rules': (u) => u.can_configure_system || isOperationsOrAdmin(u),
    '/settings/sms': (u) => u.can_send_sms || isOperationsOrAdmin(u),
  };

  const checkFunc = routePermissions[route];
  if (!checkFunc) return true; // Allow access to unlisted routes

  return checkFunc(user);
};

export const permissions = {
  hasPermission,
  canViewCustomers,
  canEditCustomers,
  canCreateCases,
  canCloseCases,
  canSendSMS,
  canViewTelemetry,
  canManageStaff,
  canConfigureSystem,
  hasRole,
  isAdmin,
  isOperationsOrAdmin,
  isComplianceOrAdmin,
  getDefaultPermissionsForRole,
  canAccessRoute,
};

